import { API_PRIMARY, API_FALLBACK, setApiUrl } from './common';

/** 1 — нет связи / таймаут; 2 — ошибка авторизации; 3 — ошибка сервера */
export const ERR = {
  NETWORK: 1,
  AUTH: 2,
  SERVER: 3,
};

export const ERR_LABELS = {
  [ERR.NETWORK]: 'Нет связи с сервером',
  [ERR.AUTH]: 'Ошибка авторизации',
  [ERR.SERVER]: 'Ошибка сервера',
};

const DEFAULT_TIMEOUT_MS = 6000;
/** Сколько раз бить в основной (.ru), потом столько же в запасной (.online) */
const ATTEMPTS_PER_HOST = 5;

function hostLabel(base) {
  try {
    return new URL(base).host;
  } catch {
    return base;
  }
}

async function fetchOnce(baseUrl, path, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${baseUrl}${path}`, {
      ...options,
      signal: controller.signal,
    });

    if (!response.ok) {
      const err = new Error(`HTTP ${response.status}`);
      err.code = ERR.SERVER;
      err.status = response.status;
      throw err;
    }

    return await response.json();
  } catch (error) {
    if (error.code === ERR.SERVER) throw error;

    const err = new Error(error.name === 'AbortError' ? 'Timeout' : error.message);
    err.code = ERR.NETWORK;
    err.cause = error;
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Сначала ATTEMPTS_PER_HOST попыток на primary (.ru из env),
 * только после всех неудач — столько же на fallback (.online).
 */
export async function postWithFailover(path, formData, {
  timeoutMs = DEFAULT_TIMEOUT_MS,
  attemptsPerHost = ATTEMPTS_PER_HOST,
  onProgress,
} = {}) {
  const hosts = [API_PRIMARY];
  if (API_FALLBACK && API_FALLBACK !== API_PRIMARY) {
    hosts.push(API_FALLBACK);
  }

  const totalAttempts = hosts.length * attemptsPerHost;
  let lastError = { code: ERR.NETWORK, message: 'Нет связи с сервером' };
  let globalAttempt = 0;

  for (const baseUrl of hosts) {
    for (let hostAttempt = 1; hostAttempt <= attemptsPerHost; hostAttempt += 1) {
      globalAttempt += 1;

      if (onProgress) {
        onProgress({
          attempt: globalAttempt,
          maxAttempts: totalAttempts,
          hostAttempt,
          attemptsPerHost,
          baseUrl,
          host: hostLabel(baseUrl),
          errorCode: null,
        });
      }

      try {
        const data = await fetchOnce(baseUrl, path, {
          method: 'POST',
          body: formData,
        }, timeoutMs);

        setApiUrl(baseUrl);

        if (data && data.res === false) {
          return {
            ...data,
            errorCode: ERR.AUTH,
            attempt: globalAttempt,
            maxAttempts: totalAttempts,
          };
        }

        return {
          ...data,
          errorCode: null,
          attempt: globalAttempt,
          maxAttempts: totalAttempts,
          baseUrl,
        };
      } catch (error) {
        lastError = {
          code: error.code || ERR.NETWORK,
          message: error.message,
        };

        if (onProgress) {
          onProgress({
            attempt: globalAttempt,
            maxAttempts: totalAttempts,
            hostAttempt,
            attemptsPerHost,
            baseUrl,
            host: hostLabel(baseUrl),
            errorCode: lastError.code,
          });
        }
      }
    }
  }

  return {
    res: false,
    errorCode: lastError.code,
    message: `${ERR_LABELS[lastError.code] || 'Ошибка'} (код ${lastError.code}). Попыток: ${totalAttempts}`,
    attempt: totalAttempts,
    maxAttempts: totalAttempts,
  };
}
